import { ErrorResponse, UserManager, WebStorageStateStore } from "oidc-client-ts";
import type { User } from "oidc-client-ts";
import { NetworkError } from "../../api/errors";
import type { OidcSettings } from "../../app/config";
import { isLocale } from "../i18n/translate";
import type { Locale } from "../i18n/translate";
import { normalizeLoginHint, sanitizeReturnPath } from "./AuthService";
import type { AuthService, AuthUser, AuthUserListener } from "./AuthService";

/** Seconds before expiry at which an access token is renewed. */
const renewalMarginSeconds = 30;

/** Path of the login callback route. */
export const loginCallbackPath = "/auth/callback";

/** Arguments of the login redirect the service passes to the identity provider. */
export interface SigninRedirectArgs {
  /** Application state restored after the redirect back. */
  readonly state: unknown;
  /** Work e-mail address (`login_hint`). */
  readonly login_hint?: string;
  /** `login` asks for a new sign-in despite an active session. */
  readonly prompt?: string;
  /** Language of the sign-in pages (`ui_locales`). */
  readonly ui_locales?: string;
}

/**
 * Returns the `ui_locales` value for the identity provider (F-87, ADR-0211): the language of the
 * app if it is one the realm offers (`de`, `en`, ADR-0198); otherwise nothing, so Keycloak picks the
 * language itself and falls back to German.
 *
 * @param locale - Current language of the app.
 * @returns The arguments to add to the login redirect.
 */
export function uiLocalesArgs(locale: unknown): { readonly ui_locales?: string } {
  const args = isLocale(locale) ? { ui_locales: locale } : {};

  return args;
}

/**
 * The part of `UserManager` that the service uses (allows fakes in tests).
 */
export interface OidcUserManager {
  getUser(): Promise<User | null>;
  signinRedirect(args: SigninRedirectArgs): Promise<void>;
  signinRedirectCallback(url: string): Promise<User>;
  signinSilent(): Promise<User | null>;
  removeUser(): Promise<void>;
  signoutRedirect(args: { id_token_hint?: string }): Promise<void>;
  readonly events: {
    addUserLoaded(callback: (user: User) => void): () => void;
    addUserUnloaded(callback: () => void): () => void;
    addUserSignedOut(callback: () => void): () => void;
  };
}

/** Supplies the current time in epoch seconds (injected for deterministic tests). */
export type EpochSecondsClock = () => number;

/**
 * Creates the `UserManager` for the browser.
 *
 * The session is stored in `localStorage` so that an installed PWA can record offline after a
 * restart (ADR-0023). PKCE with S256 is the library default for the code flow.
 *
 * @param settings - OIDC settings.
 * @param origin - The application origin, for example `https://app.example`.
 * @returns The user manager.
 */
export function createBrowserUserManager(settings: OidcSettings, origin: string): UserManager {
  const manager = new UserManager({
    authority: settings.authority,
    client_id: settings.clientId,
    scope: settings.scope,
    response_type: "code",
    redirect_uri: `${origin}${loginCallbackPath}`,
    post_logout_redirect_uri: `${origin}/`,
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    automaticSilentRenew: false,
    monitorSession: false,
  });

  return manager;
}

/**
 * Maps the OIDC user to the application user.
 *
 * @param user - The OIDC user.
 * @returns The application user.
 */
function toAuthUser(user: User): AuthUser {
  const profile = user.profile;
  const name = profile.name ?? profile.preferred_username ?? null;
  const authUser: AuthUser = { subject: profile.sub, displayName: name };

  return authUser;
}

/**
 * {@link AuthService} based on `oidc-client-ts` (ADR-0023).
 */
export class OidcAuthService implements AuthService {
  private readonly manager: OidcUserManager;

  private readonly now: EpochSecondsClock;

  private renewal: Promise<User | null> | null = null;

  private signingOut = false;

  /** @inheritdoc */
  readonly emailFirst: boolean;

  /**
   * Creates the service.
   *
   * @param manager - The user manager.
   * @param now - Clock in epoch seconds.
   * @param emailFirst - Whether the sign-in page asks for the work e-mail address first.
   */
  constructor(manager: OidcUserManager, now: EpochSecondsClock, emailFirst = false) {
    this.manager = manager;
    this.now = now;
    this.emailFirst = emailFirst;
  }

  /** @inheritdoc */
  async getUser(): Promise<AuthUser | null> {
    const user = await this.manager.getUser();
    const authUser = user === null ? null : toAuthUser(user);

    return authUser;
  }

  /** @inheritdoc */
  async getAccessToken(): Promise<string | null> {
    const user = await this.manager.getUser();

    if (user === null) {
      return null;
    }

    const expiresAt = user.expires_at ?? 0;

    if (expiresAt - renewalMarginSeconds > this.now()) {
      return user.access_token;
    }

    if (user.refresh_token === undefined) {
      // Without a refresh token the session cannot be renewed silently: sign in again.
      await this.manager.removeUser();
      return null;
    }

    const renewed = await this.renew();
    const token = renewed?.access_token ?? null;

    return token;
  }

  /** @inheritdoc */
  async login(returnTo: string, locale: Locale, loginHint?: string): Promise<void> {
    if (this.signingOut) {
      // The route guard reacts to the removed session; a login redirect would win the race
      // against the end session redirect and sign the person in again through the provider's
      // still active session.
      return;
    }

    const state = { returnTo: sanitizeReturnPath(returnTo) };
    const hint = loginHint === undefined ? null : normalizeLoginHint(loginHint);
    // With organizations, Keycloak routes the address to the customer's identity provider.
    const args: SigninRedirectArgs =
      hint === null
        ? { state, ...uiLocalesArgs(locale) }
        : { state, ...uiLocalesArgs(locale), login_hint: hint };
    await this.manager.signinRedirect(args);
  }

  /** @inheritdoc */
  async confirmSignIn(returnTo: string, locale: Locale): Promise<void> {
    if (this.signingOut) {
      return;
    }

    const state = { returnTo: sanitizeReturnPath(returnTo) };
    // `prompt=login` makes Keycloak skip the session cookie and run the whole browser flow again,
    // with the one-time code of operator staff (ADR-0194); the new token names the instant in
    // `auth_time`.
    await this.manager.signinRedirect({ state, prompt: "login", ...uiLocalesArgs(locale) });
  }

  /** @inheritdoc */
  async completeLogin(url: string): Promise<string> {
    const user = await this.manager.signinRedirectCallback(url);
    const state: unknown = user.state;
    const returnTo =
      typeof state === "object" && state !== null && "returnTo" in state
        ? sanitizeReturnPath(state.returnTo)
        : "/";

    return returnTo;
  }

  /** @inheritdoc */
  async logout(): Promise<void> {
    this.signingOut = true;

    try {
      const user = await this.manager.getUser();
      await this.manager.removeUser();

      if (user?.id_token !== undefined) {
        await this.endProviderSession(user.id_token);
      }
    } finally {
      // Reached only if the browser stays on the page (offline, no ID token, native browser tab):
      // the route guard then offers the login button instead of redirecting.
      this.signingOut = false;
    }
  }

  /** @inheritdoc */
  subscribe(listener: AuthUserListener): () => void {
    const removeLoaded = this.manager.events.addUserLoaded((user) => {
      listener(toAuthUser(user));
    });
    const removeUnloaded = this.manager.events.addUserUnloaded(() => {
      listener(null);
    });
    const removeSignedOut = this.manager.events.addUserSignedOut(() => {
      listener(null);
    });
    const unsubscribe = (): void => {
      removeLoaded();
      removeUnloaded();
      removeSignedOut();
    };

    return unsubscribe;
  }

  /**
   * Redirects to the end session endpoint of the identity provider.
   *
   * @param idToken - The ID token of the ended session.
   */
  private async endProviderSession(idToken: string): Promise<void> {
    try {
      await this.manager.signoutRedirect({ id_token_hint: idToken });
    } catch {
      // Offline: the local session is gone; the provider session expires on its own.
    }
  }

  /**
   * Renews the tokens once at a time: with refresh token rotation, parallel refreshes would
   * invalidate each other.
   *
   * @returns The renewed user or `null` if the session ended.
   */
  private renew(): Promise<User | null> {
    this.renewal ??= this.renewOnce().finally(() => {
      this.renewal = null;
    });

    return this.renewal;
  }

  private async renewOnce(): Promise<User | null> {
    try {
      const user = await this.manager.signinSilent();
      return user;
    } catch (error) {
      if (error instanceof ErrorResponse) {
        // The provider rejected the refresh (session ended or token revoked): sign out locally.
        await this.manager.removeUser();
        return null;
      }

      throw new NetworkError(error);
    }
  }
}
